import api, { ApiResponse } from './index'

export interface LikeStatus {
    liked: boolean
    likesCount: number
    targetId: string
    targetType: 'user' | 'gig' | 'review'
}

export interface LikeTarget {
    targetId: string
    targetType: 'user' | 'gig' | 'review'
}

export interface Like {
    _id: string
    liker: {
        _id: string
        profile: {
            firstName: string
            lastName: string
            avatar?: string
        }
        role: string
        createdAt: string
    }
    target: string
    targetType: 'user' | 'gig' | 'review'
    createdAt: string
    updatedAt: string
}

export interface Liker {
    _id: string
    profile: {
        firstName: string
        lastName: string
        avatar?: string
        bio?: string
    }
    role: string
    createdAt: string
    likedAt: string
}

export const likeApi = {
    // Toggle like/unlike
    toggleLike: (data: { targetId: string, targetType: 'user' | 'gig' | 'review' }): Promise<ApiResponse<LikeStatus>> =>
        api.post('/likes/toggle', data),

    // Get like status for a target
    getLikeStatus: (targetType: string, targetId: string): Promise<ApiResponse<LikeStatus>> =>
        api.get(`/likes/status/${targetType}/${targetId}`),

    // Get all likes for a target
    getLikes: (
        targetType: string,
        targetId: string,
        params: { page?: number, limit?: number } = {}
    ): Promise<ApiResponse<{
        likes: Like[]
        totalCount: number
        currentPage: number
        totalPages: number
    }>> =>
        api.get(`/likes/${targetType}/${targetId}`, { params }),

    // Get who liked something (likers)
    getLikers: (
        targetType: string,
        targetId: string,
        params: { page?: number, limit?: number } = {}
    ): Promise<ApiResponse<{
        likers: Liker[]
        totalCount: number
        currentPage: number
        totalPages: number
    }>> =>
        api.get(`/likes/${targetType}/${targetId}/likers`, { params }),

    // Get user's likes (things they liked)
    getUserLikes: (
        userId?: string,
        params: { targetType?: string, page?: number, limit?: number } = {}
    ): Promise<ApiResponse<{
        likes: Like[]
        totalCount: number
        currentPage: number
        totalPages: number
    }>> =>
        userId
            ? api.get(`/likes/user/${userId}`, { params })
            : api.get('/likes/user', { params }),

    // Batch like status check
    getBatchLikeStatus: (targets: LikeTarget[]): Promise<ApiResponse<{
        results: LikeStatus[]
    }>> =>
        api.post('/likes/batch-status', { targets }),

    // Remove like (unlike)
    removeLike: (targetType: string, targetId: string): Promise<ApiResponse<LikeStatus>> =>
        api.delete(`/likes/${targetType}/${targetId}`),

    // Convenience methods for specific target types
    toggleUserLike: (userId: string): Promise<ApiResponse<LikeStatus>> =>
        api.post('/likes/toggle', { targetId: userId, targetType: 'user' }),

    toggleGigLike: (gigId: string): Promise<ApiResponse<LikeStatus>> =>
        api.post('/likes/toggle', { targetId: gigId, targetType: 'gig' }),

    toggleReviewLike: (reviewId: string): Promise<ApiResponse<LikeStatus>> =>
        api.post('/likes/toggle', { targetId: reviewId, targetType: 'review' }),

    getUserLikeStatus: (userId: string): Promise<ApiResponse<LikeStatus>> =>
        api.get(`/likes/status/user/${userId}`),

    getGigLikeStatus: (gigId: string): Promise<ApiResponse<LikeStatus>> =>
        api.get(`/likes/status/gig/${gigId}`),

    getReviewLikeStatus: (reviewId: string): Promise<ApiResponse<LikeStatus>> =>
        api.get(`/likes/status/review/${reviewId}`)
}